import React, { FC } from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Collapse } from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import ListItem from '@mui/material/ListItem';
import { useTranslation } from 'react-i18next';
import CaretDownWhite from '../../assets/icons/caret-down-white.svg';
import LockIconWhite from '../../assets/icons/lock-icon-white.svg';
import { useSidebarStore } from '../../store/sidebarStore';

interface NavBarWithSubMenuProps {
  item: any,
  setOpenedSubMenu: (_label: string) => void,
  handleSubMenuClick: (_label: string) => void,
}

const NavbarWithSubMenu: FC<NavBarWithSubMenuProps> = (props) => {
  const isSidebarOpen = useSidebarStore((state) => state.isSidebarOpen);
  const setIsSidebarOpen = useSidebarStore((state) => state.setIsSidebarOpen);
  const location = useLocation();
  const { t } = useTranslation();
  const isListActive = location.pathname.startsWith(props.item.href);

  return (
    <ListItem
      key={props.item.label}
      disablePadding
      sx={{ display: 'block' }}
    >
      <ListItemButton
        sx={{
          minHeight: 44,
          mx: 1,
          borderRadius: '8px',
          justifyContent: isSidebarOpen ? 'initial' : 'center',
          pl: isSidebarOpen ? '14px' : 0,
          pr: isSidebarOpen ? '10px' : 0,
          transition: 'background-color 0.15s ease',
          backgroundColor: isListActive ? '#0C485E' : 'transparent',
          color: isListActive ? 'white' : '#374151',
          '&:hover': {
            backgroundColor: isListActive ? '#0d5270' : 'rgba(12,72,94,0.05)',
          },
        }}
        onClick={() => {
          if (!isSidebarOpen) {
            setIsSidebarOpen(true);
            props.setOpenedSubMenu(props.item.href);
          } else {
            props.handleSubMenuClick(props.item.href);
          }
        }}
        data-cy={`nav${props.item.href}`}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: isSidebarOpen ? '14px' : 0,
            justifyContent: 'center',
          }}
        >
          <img
            src={isListActive ? props.item.iconSecondary : props.item.iconPrimary}
            alt=""
            className={`h-4 w-4 object-contain ${isListActive ? '' : 'opacity-70'}`}
          />
        </ListItemIcon>

        {isSidebarOpen && (
          <>
            <p className={`text-sm ${isListActive ? 'font-medium' : 'font-normal opacity-80'}`}>
              {t(props.item.label)}
            </p>
            <img
              src={CaretDownWhite}
              alt="caret"
              className={`ml-auto h-3 w-3 flex-shrink-0 duration-200 ${props.item.isOpen ? 'rotate-180' : ''} ${
                isListActive ? 'opacity-70' : '[filter:brightness(0)] opacity-30'
              }`}
            />
          </>
        )}
      </ListItemButton>

      <Collapse in={props.item.isOpen} timeout="auto" unmountOnExit>
        <div className="pb-1 pt-0.5">
          {props.item.subList.map((subItem: any) => {
            if (subItem.hide) return null;
            const isSubListActive = location.pathname.split('/')[2] === subItem.href.split('/')[0];
            return (
              <NavLink
                key={subItem.href}
                onClick={() => {
                  if (isSubListActive) {
                    props.handleSubMenuClick(props.item.href);
                    setIsSidebarOpen(false);
                  }
                }}
                to={`${!subItem.isLocked ? `${props.item.href}/${subItem.href}` : '/settings/subscription'}`}
                className="block"
                data-cy={`nav${props.item.href}/${subItem.href}`}
              >
                <ListItemButton
                  sx={{
                    minHeight: 36,
                    mx: 1,
                    ml: '36px',
                    borderRadius: '6px',
                    pl: '10px',
                    pr: '10px',
                    mt: '1px',
                    backgroundColor: isSubListActive ? 'rgba(12,72,94,0.08)' : 'transparent',
                    color: isSubListActive ? '#0C485E' : '#6b7280',
                    borderLeft: isSubListActive ? '2px solid #0C485E' : '2px solid transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(12,72,94,0.06)',
                      color: '#0C485E',
                    },
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: '10px' }}>
                    <img
                      src={subItem.iconWhite}
                      alt=""
                      className={`h-3.5 w-3.5 object-contain [filter:brightness(0)] ${isSubListActive ? 'opacity-70' : 'opacity-40'}`}
                    />
                  </ListItemIcon>
                  <p className={`text-xs ${isSubListActive ? 'font-medium' : 'font-normal'}`}>
                    {t(subItem.label)}
                  </p>
                  {subItem.isLocked && (
                    <div className="group ml-auto grid h-5 w-5 place-content-center rounded border border-gray-300 transition-all hover:w-14">
                      <img src={LockIconWhite} alt="lock" className="h-2.5 w-2.5 object-contain [filter:brightness(0)] opacity-50 group-hover:hidden" />
                      <p className="hidden text-[8px] text-gray-500 group-hover:block">Upgrade</p>
                    </div>
                  )}
                </ListItemButton>
              </NavLink>
            );
          })}
        </div>
      </Collapse>
    </ListItem>
  );
};

export default NavbarWithSubMenu;
